import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CustomWalletModal } from './CustomWalletModal';
import { useAccount, useDisconnect } from 'wagmi';
import './WalletButton.css';

const WalletButton: React.FC = () => {
  const { address, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  
  const [showModal, setShowModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleClick = () => {
    if (isConnected) {
      setShowDropdown(!showDropdown);
    } else {
      setShowModal(true);
    }
  };

  const handleDisconnect = async () => {
    try {
      disconnect();
      setShowDropdown(false);
    } catch (error) {
      console.error('Error disconnecting:', error);
    }
  };

  const copyAddress = (walletAddress: string) => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
    }
  };

  const getDisplayName = () => {
    if (address) {
      return `${address.slice(0, 4)}...${address.slice(-4)}`;
    }
    return 'Connected';
  };

  return (
    <>
      <div className="relative">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleClick}
          className="wallet-adapter-button"
        >
          {isConnected ? (
            <div className="flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">●</span>
              <span>{getDisplayName()}</span>
            </div>
          ) : (
            <span>Connect Wallet</span>
          )}
        </motion.button>

        {/* Dropdown for connected state */}
        {isConnected && showDropdown && address && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute top-full right-0 mt-2 w-80 bg-slate-800 border border-blue-500/20 rounded-xl shadow-xl p-4 z-50"
          >
            {/* User Info */}
            <div className="mb-4 pb-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                  <span className="text-lg">👤</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">{getDisplayName()}</p>
                  <p className="text-xs text-gray-400">Wallet connected</p>
                </div>
              </div>
            </div>

            {/* Connected Wallet */}
            <div className="space-y-3 mb-4">
              <div className="p-3 bg-white/5 rounded-lg border border-green-500/20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-green-400">MetaMask Wallet</span>
                  <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded">Active</span>
                </div>
                <div className="text-xs text-gray-400 font-mono mb-2">
                  {address.slice(0, 10)}...{address.slice(-8)}
                </div>
                <button
                  onClick={() => copyAddress(address)}
                  className="text-xs px-3 py-1.5 text-blue-400 hover:bg-blue-500/10 rounded-lg transition-colors border border-blue-500/20"
                >
                  Copy Address
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <button
                onClick={handleDisconnect}
                className="w-full text-xs px-3 py-2.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors border border-red-500/20 font-semibold"
              >
                Disconnect
              </button>
            </div>
          </motion.div>
        )}

        {/* Click outside to close dropdown */}
        {showDropdown && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowDropdown(false)}
          />
        )}
      </div>

      {/* Custom Wallet Modal */}
      <CustomWalletModal
        visible={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
};

export default WalletButton;
